/*
 * Copyright (C) Fraunhofer IESE 2021-2024 - Pedram (Majid) Jokar, Mher Ter-Tovmasyan,
 * Bestin John, Steffen Hupp, Anna Kleiner, Rafael Aranda Lopez King, Emily Calvet, Philipp Ewen,
 * Matthias Gerbershagen, Milad Chatrangoon, Stefan Schweitzer
 *
 * SPDX-License-Identifier: AGPL-3.0-or-later
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. If not, see <https://www.gnu.org/licenses/>.
 */

import { yupResolver } from "@hookform/resolvers/yup";
import { Ownership } from "@slr/media-service-sdk";
import {
  SLRFormInfo,
  SLRLogoEditorLabels,
  SLRLogoUpload,
  SLRModal,
  emptyAsNull,
  getIcon,
  isEmptyOrNull,
  showErrorToast,
  useUpdateMediaItems,
} from "@slr/shared-library";
import { Organization, OrganizationType } from "@slr/user-service-external-sdk";
import { useChangeOrganization, useCreateOrganization } from "hooks";
import { FC, useCallback, useMemo, useState } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { OrganizationInput, OrganizationInputSchema } from "..";

import "./OrganizationModal.scss";

type OrganizationModalProps = {
  suggestedOrganizationName?: string;
  organization?: Organization;
  buttonLabel?: string;
  onCreate?: (id: string) => void;
  onClose: () => void;
};

export const OrganizationModal: FC<OrganizationModalProps> = ({
  suggestedOrganizationName,
  organization,
  buttonLabel,
  onCreate,
  onClose,
}) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "organizationsPage.createEdit",
  });

  const { mutate: createOrganization, isPending: isCreateOrganizationLoading } =
    useCreateOrganization(onCreate === undefined);
  const { mutate: changeOrganization, isPending: isUpdateOrganizationLoading } =
    useChangeOrganization();

  const { mutate: updateMedia } = useUpdateMediaItems({
    onDeleteError: () => showErrorToast("toasts.deleteMediaItem.error"),
    onSaveError: () => showErrorToast("toasts.saveMediaItem.error"),
  });

  const [isLogoDialogBusy, setIsLogoDialogBusy] = useState<boolean>(false);

  const isLoading = useMemo(
    () => isCreateOrganizationLoading || isUpdateOrganizationLoading,
    [isCreateOrganizationLoading, isUpdateOrganizationLoading]
  );

  const isEdit = useMemo(() => organization !== undefined, [organization]);

  /**
   * NOTE: Currently, user portal only supports the creation of user organization, however organizations of other types can be edited here
   * As other organizations require an email address we need to pass this information to the schema
   */
  const isUserOrganization = useMemo(
    () => Boolean(organization?.type === OrganizationType.User) || !isEdit,
    [isEdit, organization?.type]
  );

  const {
    formState: { errors },
    handleSubmit,
    control,
    register,
    reset,
  } = useForm<OrganizationInput>({
    mode: isEdit ? "onChange" : "onSubmit",
    defaultValues: {
      ...organization,
      name: suggestedOrganizationName ?? organization?.name ?? null,
      mediaItemId: organization?.logoId ?? null,
      taxNumber: organization?.taxNumber ?? null,
      description: organization?.description ?? null,
      detailedDescription: organization?.detailedDescription ?? null,
    },
    resolver: yupResolver(OrganizationInputSchema(isUserOrganization)),
  });

  const handleCreateUpdateOrganization = useCallback(
    (data: OrganizationInput) => {
      const payload: OrganizationInput = {
        ...data,
        description: organization?.description ?? null,
        detailedDescription: organization?.detailedDescription ?? null,
      };
      if (isEdit && organization) {
        changeOrganization(
          {
            organizationId: organization.id,
            // eslint-disable-next-line
            // @ts-ignore
            changeOrganizationRequestDto: payload,
          },
          {
            onSuccess: () => {
              updateMedia({
                newMediaIds: !isEmptyOrNull(payload.mediaItemId)
                  ? [payload.mediaItemId]
                  : [],
                oldMediaIds: !isEmptyOrNull(organization.logoId)
                  ? [organization.logoId]
                  : [],
              });
              onClose();
            },
          }
        );
      } else {
        createOrganization(
          {
            organizationType: OrganizationType.User,
            // eslint-disable-next-line
            // @ts-ignore
            createOrganizationRequestDto: payload,
          },
          {
            onSuccess: ({ id }) => {
              updateMedia({
                newMediaIds: !isEmptyOrNull(payload.mediaItemId)
                  ? [payload.mediaItemId]
                  : [],
              });
              if (onCreate) onCreate(id);
              onClose();
            },
          }
        );
      }
    },
    [
      changeOrganization,
      createOrganization,
      isEdit,
      onClose,
      onCreate,
      organization,
      updateMedia,
    ]
  );

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [onClose, reset]);

  return (
    <SLRModal
      isLoading={isLoading}
      enforceFocus={false}
      backdrop="static"
      title={t(isEdit ? "edit" : "create")}
      size="lg"
      fullscreen="md-down"
      isOpen
      onClose={handleClose}
      footerContent={
        <Row className="w-100 m-0 gap-3 align-items-center justify-content-end">
          <Col xs="auto">
            <SLRFormInfo className="w-auto" text={t("mandatoryField")} />
          </Col>
          <Col xs="12" sm="auto" className="p-0 text-end">
            <Button
              type="submit"
              size="lg"
              onClick={handleSubmit(handleCreateUpdateOrganization)}
              disabled={isLoading || isLogoDialogBusy}
            >
              {buttonLabel ?? t(isEdit ? "save" : "create")}
            </Button>
          </Col>
        </Row>
      }
      onHide={handleClose}
    >
      <Form
        noValidate
        id="create-organization"
        className="organization-modal px-md-3 px-lg-4"
        onSubmit={handleSubmit(handleCreateUpdateOrganization)}
      >
        <Row className="mb-md-4 pt-4">
          <Col xs="12">
            <h2 className="mb-3">{t(isEdit ? "edit" : "new")}</h2>
          </Col>
          <Col xs="12" className="picture-editor-wrapper text-lg-end">
            <Form.Label htmlFor="logo" aria-hidden className="d-none">
              {t("logoPicture")}
            </Form.Label>
            <SLRLogoUpload
              id="logo"
              name="mediaItemId"
              control={control}
              mediaOwner={{
                organizationId: isEdit ? organization?.id : undefined,
                owner: isEdit ? Ownership.Organization : Ownership.User,
              }}
              icons={{
                fallbackIcon: getIcon("fas", "image"),
                uploadIcon: getIcon("fas", "cloud-upload"),
                deleteIcon: getIcon("fas", "trash-can"),
                rotateIcon: getIcon("fas", "arrows-rotate"),
              }}
              labels={
                t("logo", {
                  returnObjects: true,
                }) as SLRLogoEditorLabels
              }
              isBusy={setIsLogoDialogBusy}
            />
          </Col>
          <Col xs="12" lg="8">
            <div>
              <Form.Label htmlFor="name">{t("name")}*</Form.Label>
              <Form.Control
                id="name"
                type="text"
                className="form-control-lg"
                autoComplete="true"
                isInvalid={!!errors.name}
                disabled={isLoading}
                {...register("name")}
              />
              <SLRFormInfo
                text={errors?.name?.message}
                isInvalid={!!errors.name}
              />
            </div>
          </Col>
          <Col xs="12" lg="7">
            <Form.Group controlId="websiteUrl" className="mt-4">
              <Form.Label>{t("websiteUrl")}</Form.Label>
              <Form.Control
                type="url"
                className="form-control-lg"
                isInvalid={!!errors.websiteUrl}
                disabled={isLoading}
                {...register("websiteUrl", {
                  setValueAs: (value: string) =>
                    !isEmptyOrNull(value) && !value.startsWith("http")
                      ? "https://" + value
                      : emptyAsNull(value),
                })}
              />
              <SLRFormInfo
                text={errors?.websiteUrl?.message}
                isInvalid={!!errors.websiteUrl}
              />
            </Form.Group>
          </Col>
          <Col xs="12" lg="7">
            <Form.Group controlId="email" className="mt-4">
              <Form.Label>
                {t("email")}
                {isUserOrganization ? "" : "*"}
              </Form.Label>
              <Form.Control
                type="text"
                className="form-control-lg"
                autoComplete="true"
                isInvalid={!!errors.email}
                disabled={isLoading}
                {...register("email", {
                  setValueAs: emptyAsNull,
                })}
              />
              <SLRFormInfo
                text={errors?.email?.message}
                isInvalid={!!errors.email}
              />
            </Form.Group>
          </Col>
          <Col xs="12" lg="7">
            <Form.Group controlId="phoneNumber" className="mt-4">
              <Form.Label>{t("phoneNumber")}</Form.Label>
              <Form.Control
                type="text"
                className="form-control-lg"
                isInvalid={!!errors.phoneNumber}
                disabled={isLoading}
                {...register("phoneNumber", {
                  setValueAs: emptyAsNull,
                })}
              />
              <SLRFormInfo
                text={errors?.phoneNumber?.message}
                isInvalid={!!errors.phoneNumber}
              />
            </Form.Group>
          </Col>
          <Col xs="12" lg="7">
            <Form.Group controlId="address" className="mt-4">
              <Form.Label>{t("address")}</Form.Label>
              <Form.Control
                type="text"
                className="form-control-lg"
                autoComplete="false"
                isInvalid={!!errors.address}
                disabled={isLoading}
                {...register("address", {
                  setValueAs: emptyAsNull,
                })}
              />
              <SLRFormInfo
                text={errors?.address?.message}
                isInvalid={!!errors.address}
              />
            </Form.Group>
          </Col>
          <Col xs="12" lg="7">
            <Form.Group controlId="taxNumber" className="mt-4">
              <Form.Label>{t("vatNumber")}</Form.Label>
              <Form.Control
                type="text"
                className="form-control-lg"
                isInvalid={!!errors.taxNumber}
                disabled={isLoading}
                {...register("taxNumber", {
                  setValueAs: emptyAsNull,
                })}
              />
              <SLRFormInfo
                text={errors?.taxNumber?.message}
                isInvalid={!!errors.taxNumber}
              />
            </Form.Group>
          </Col>
        </Row>
        {/* Add an invisible form submit button to ensure pressing enter submits the form */}
        <input
          type="submit"
          className="d-none"
          aria-hidden="true"
          title={t(isEdit ? "edit" : "new")}
        />
      </Form>
    </SLRModal>
  );
};
